import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid'
import { type LoaderFunctionArgs } from '@remix-run/node'
import {
	json,
	Form,
	useLoaderData,
	useNavigate,
	useSubmit,
	useSearchParams,
} from '@remix-run/react'
import { useEffect } from 'react'
import { useDebounceFetcher } from 'remix-utils/use-debounce-fetcher'
import ComboBox from '#app/components/ComboBox.tsx'
import SelectInput from '#app/components/Select'
import SimpleTable from '#app/components/SimpleTable'
import { getFiltered } from '#app/queries/RankingAllData-queries'
import { type loader as GeoLoader } from '#app/routes/api+/athlete+/get-geo.ts'
import { Card } from '@radix-ui/themes'
import { toCamelCase } from '#app/utils/misc.tsx'

export async function loader({ request }: LoaderFunctionArgs) {
	const searchParams = new URL(request.url).searchParams
	const age_group = searchParams.get('age')?.toString() || 'all'
	const sex = searchParams.get('sex')?.toString() || 'all'
	const state = searchParams.get('state')?.toString() || 'all'

	const peers = await getFiltered({ age_group, sex, state })
	return json({ peers, ageClass: age_group, sex: sex, state: state })
}
export default function TopRunners() {
	const data = useLoaderData<typeof loader>()
	const geoFetcher = useDebounceFetcher<typeof GeoLoader>()
	const submit = useSubmit()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const state = searchParams.get('state')?.toString()

	useEffect(() => {
		if (state) {
			geoFetcher.submit({ state: state }, { action: '/api/athlete/get-geo', debounceTimeout: 100 })
		}
	}, [state])

	function handleRowClick(entry: { id: string }) {
		navigate(`/athlete/${entry.id}`)
	}

	const mapping = {
		Runner: 'Runner',
		Country: 'country',
		Change: 'rankChange',
		Time: 'Time',
	}

	const stateOptions =
		geoFetcher.data?.states?.map(item => ({
			value: item.state,
			label: toCamelCase(item.state),
			id: item.id,
		})) || []

	const _peers = data?.peers.map(peer => {
		const { rankChange } = peer
		let icon
		if (rankChange > 0) icon = <ArrowUpIcon width="1rem" height="1rem" />
		if (rankChange < 0) icon = <ArrowDownIcon width="1rem" height="1rem" />
		if (rankChange === 0) icon = <span className="text-gray-500">&mdash;</span>

		const _rankChange =
			Math.abs(rankChange) > 0 ? (
				<span className="flex items-center gap-x-1">
					{icon}
					<span>
						{rankChange > 0 ? '+' : '-'}
						{peer.rankChange}
					</span>
				</span>
			) : (
				icon
			)
		return {
			...peer,
			rankChange: _rankChange,
		}
	})

	function onStateSelection(item: { value: string; label: string; id: string } | null) {
		if (!item) return
		setSearchParams(params => {
			const state = params.get('state')?.toString()
			if (state) params.delete('state')
			params.set('state', item.value)
			return params
		})
	}

	function onTextChange(e: React.ChangeEvent<HTMLInputElement>) {
		geoFetcher.submit(
			{ state: e.target.value },
			{ action: '/api/athlete/get-geo', debounceTimeout: 100 },
		)
	}

	return (
		<div className="flex justify-center">
			<div className="flex max-w-[700px] flex-1 flex-col items-center gap-y-4">
				<Card className="w-full">
					<div className="pb-4 text-h5">Search by Classification</div>
					<Form
						method="GET"
						className="flex flex-col gap-y-2"
						onChange={e => submit(e.currentTarget)}
					>
						<SelectInput
							label={'Age Group'}
							name="age"
							defaultValue={data?.ageClass}
							options={[
								{ value: 'all', label: 'All' },
								{ value: '18-39', label: '18-39' },
								{ value: '40-44', label: '40-44' },
								{ value: '45-49', label: '45-49' },
								{ value: '50-54', label: '50-54' },
								{ value: '55-59', label: '55-59' },
								{ value: '60-64', label: '60-64' },
								{ value: '65-69', label: '65-69' },
								{ value: '70-74', label: '70-74' },
								{ value: '75-79', label: '75-79' },
								{ value: '80-84', label: '80-84' },
								{ value: '85-89', label: '85-89' },
								{ value: '90-94', label: '90-94' },
								{ value: '95-99', label: '95-99' },
								{ value: '100-104', label: '100-104' },
							]}
						/>
						<SelectInput
							label="Sex"
							defaultValue={data?.sex}
							options={[
								{ value: 'all', label: 'All' },
								{ value: 'm', label: 'Male' },
								{ value: 'f', label: 'Female' },
							]}
							name="sex"
						/>
						<input type="hidden" value={data?.state} name="state" />
					</Form>
					<div className="mt-2">
						<ComboBox
							label="State"
							defaultValue={toCamelCase(data?.state)}
							options={[{ id: 'all', value: 'all', label: 'All' }, ...stateOptions]}
							onSelection={onStateSelection}
							onTextChange={onTextChange}
							renderDisplayValue={item => item.label}
							placeholder="All"
						/>
					</div>
				</Card>
				<div className="w-full pt-5">
					<SimpleTable
						columnToHighlight="Runner"
						mapping={mapping}
						data={_peers}
						onRowClick={handleRowClick}
					/>
				</div>
			</div>
		</div>
	)
}
